import React, { useRef, useState, useCallback, useEffect } from "react"
import { useOutsideClick } from "./useOutsideClick"
import { isElementVisible } from "common/lib/isElementVisible"

const KEY_MAPPINGS = {
  ESCAPE: { key: "Escape", keyCode: 27 },
  ARROW_UP: { key: "ArrowUp", keyCode: 38 },
  ARROW_DOWN: { key: "ArrowDown", keyCode: 40 },
  TAB: { key: "Tab", keyCode: 9 },
  SPACE: { key: " ", keyCode: 32 },
  ENTER: { key: "Enter", keyCode: 13 },
}

const keycodeLookup = (keyCodeToLookup: number) => {
  return Object.values(KEY_MAPPINGS).find(({ keyCode }) => keyCodeToLookup === keyCode)?.key || ""
}

/**
 * This hook manages the state of a dropdown with keyboard accessibility support.
 *
 * It returns a `ref` for the dropdown, an `onClick` callback, and an `isActive` value which is
 * toggled whenever the dropdown's active state changes.
 *
 * @deprecated `useDropdown` usage is deprecated. Use `common/components/Dropdown` instead.
 * Or if you need more fine-grained customisation, check `common/components/Overlay`.
 *
 * Supported keyboard interactions:
 * - Enter/Space: Open dropdown, activate focused item
 * - Arrow Up/Down: Navigate through items (can't escape menu)
 * - Tab/Shift+Tab: Navigate forward/backward through items (can escape menu)
 * - Escape: Close dropdown and return focus to trigger
 */

export const useDropdown = () => {
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const [isActive, setActive] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const getMenuItems = useCallback((): HTMLElement[] => {
    const allMenuItems = ref.current?.querySelectorAll("[role='menuitem']") ?? []
    return Array.from(allMenuItems).filter((item) =>
      isElementVisible(item as HTMLElement),
    ) as HTMLElement[]
  }, [])

  const getTrigger = useCallback((): HTMLElement | null => {
    // Resolve the trigger lazily so it's available even before the effect
    // that caches it has run (e.g. on the very first open).
    if (!triggerRef.current) {
      triggerRef.current = ref.current?.querySelector("button") ?? null
    }
    return triggerRef.current
  }, [])

  const setFocusedIndexAndFocus = useCallback(
    (index: number) => {
      const items = getMenuItems()
      if (items.length === 0) return

      const clampedIndex = Math.max(0, Math.min(index, items.length - 1))
      setFocusedIndex(clampedIndex)
      items[clampedIndex]?.focus({ focusVisible: true } as FocusOptions)
    },
    [getMenuItems],
  )

  const closeMenu = useCallback(() => {
    setActive(false)
    setFocusedIndex(-1)
    getTrigger()?.setAttribute("aria-expanded", "false")
  }, [getTrigger])

  const focusTrigger = useCallback(
    (focusVisible = false) => {
      setTimeout(() => getTrigger()?.focus({ focusVisible: focusVisible } as FocusOptions), 0)
    },
    [getTrigger],
  )

  const openMenu = useCallback(
    (isKeyboardEvent = false) => {
      setActive(true)
      setFocusedIndex(-1)
      focusTrigger(isKeyboardEvent)
      getTrigger()?.setAttribute("aria-expanded", "true")
    },
    [getTrigger, focusTrigger],
  )

  const activateFocusedItem = useCallback(() => {
    const focusedElement = document.activeElement as HTMLElement
    focusedElement?.click()
  }, [])

  const toggleMenu = useCallback(
    (event?: React.MouseEvent) => {
      if (isActive) {
        closeMenu()
      } else {
        const isKeyboardEvent = event && event.detail === 0

        openMenu(isKeyboardEvent)
      }
    },
    [isActive, closeMenu, openMenu],
  )

  const handleTabNavigation = useCallback(
    (currentIndex: number, itemCount: number, isShiftKey: boolean) => {
      const isAtBoundary = isShiftKey
        ? currentIndex === 0 || currentIndex === -1
        : currentIndex === itemCount - 1

      if (isAtBoundary) {
        closeMenu()
        focusTrigger(true)
      } else {
        const nextIndex = isShiftKey ? currentIndex - 1 : currentIndex + 1
        setFocusedIndexAndFocus(nextIndex)
      }
    },
    [closeMenu, focusTrigger, setFocusedIndexAndFocus],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const eventKey = event.key || keycodeLookup(event.keyCode)
      const isTriggerKey =
        eventKey === KEY_MAPPINGS.ENTER.key || eventKey === KEY_MAPPINGS.SPACE.key

      if (!isActive && !isTriggerKey) {
        return
      }

      const items = getMenuItems()

      // Note: Enter uses its browser default behaviour
      const handlers: Record<string, (ev: KeyboardEvent) => void> = {
        [KEY_MAPPINGS.SPACE.key]: (ev: KeyboardEvent) => {
          ev.preventDefault()
          isActive ? activateFocusedItem() : openMenu(true)
        },
        [KEY_MAPPINGS.ARROW_DOWN.key]: (ev: KeyboardEvent) => {
          ev.preventDefault()
          setFocusedIndexAndFocus(focusedIndex + 1)
        },
        [KEY_MAPPINGS.ARROW_UP.key]: (ev: KeyboardEvent) => {
          ev.preventDefault()
          setFocusedIndexAndFocus(focusedIndex - 1)
        },
        [KEY_MAPPINGS.TAB.key]: (ev: KeyboardEvent) => {
          ev.preventDefault()
          handleTabNavigation(focusedIndex, items.length, ev.shiftKey)
        },
        [KEY_MAPPINGS.ESCAPE.key]: (ev: KeyboardEvent) => {
          ev.preventDefault()
          closeMenu()
          focusTrigger(true)
        },
      }

      handlers[eventKey]?.(event)
    },
    [
      isActive,
      getMenuItems,
      focusedIndex,
      activateFocusedItem,
      openMenu,
      setFocusedIndexAndFocus,
      handleTabNavigation,
      closeMenu,
      focusTrigger,
    ],
  )

  const onOutsideClick = useCallback(() => closeMenu(), [closeMenu])
  useOutsideClick(ref, onOutsideClick)

  useEffect(() => {
    if (isActive) {
      triggerRef.current = ref.current?.querySelector("button") ?? null
    } else {
      triggerRef.current = null
    }

    const listener = (event: KeyboardEvent) => handleKeyDown(event)
    const current = ref.current

    current?.addEventListener("keydown", listener)

    return () => {
      current?.removeEventListener("keydown", listener)
    }
  }, [handleKeyDown, isActive])

  return { ref, isActive, onClick: toggleMenu }
}
